import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Form, Select, Button, Spin, Input, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const FilteringDrawer = ({
  closeFilteringDrawer,
  isFilteringDrawerOpened,
  resetFilter,
  onSave,
  title,
  loading,
  formName,
  bundleTypes,
  selectedOptionOfDropdownFields,
  setSelectedOptionOfDropdownFields,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [filteredFields, setFilteredFields] = useState([]);
  const { cardTypes } = useSelector((state) => state.cardTypes);

  const commonFields = useMemo(
    () => [
      {
        key: "numberOfTrips",
        label: t("bundleScreen.filterDrawer.numberOfTrips"),
        component: <Input />,
      },
    ],
    [t]
  );

  const bundleFilters = useMemo(
    () => ({
      TOP_UP_CARD_TYPE: commonFields,
      TICKET_TYPE: commonFields,
      NEW_CARD_TYPE: commonFields,
      SPECIAL_CARD_TYPE: [
        {
          key: "cardType",
          label: t("bundleScreen.filterDrawer.cardType"),
          component: (
            <Select
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {cardTypes.map((type) => (
                <Select.Option key={type.id} value={type.id}>
                  {type.name}
                </Select.Option>
              ))}
            </Select>
          ),
        },
        {
          key: "bundleValidityFrom",
          label: t("bundleScreen.filterDrawer.bundleValidityFrom"),
          component: (
            <DatePicker
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          ),
        },
        {
          key: "bundleValidityTo",
          label: t("bundleScreen.filterDrawer.bundleValidityTo"),
          component: (
            <DatePicker
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          ),
        },
      ],
    }),
    [t, commonFields, cardTypes]
  );

  // Initialize form with selectedOptionOfDropdownFields when the drawer opens
  useEffect(() => {
    if (isFilteringDrawerOpened) {
      form.setFieldsValue({
        bundleType: selectedOptionOfDropdownFields?.bundleType || null,
        cardType: selectedOptionOfDropdownFields?.cardType || null,
        numberOfTrips: selectedOptionOfDropdownFields?.numberOfTrips || null,
        bundleValidityFrom: selectedOptionOfDropdownFields?.bundleValidityFrom
          ? dayjs(selectedOptionOfDropdownFields.bundleValidityFrom, "YYYY-MM-DD")
          : null,
        bundleValidityTo: selectedOptionOfDropdownFields?.bundleValidityTo
          ? dayjs(selectedOptionOfDropdownFields.bundleValidityTo, "YYYY-MM-DD")
          : null,
      });

      // Set filtered fields based on the initial bundleType
      if (selectedOptionOfDropdownFields?.bundleType) {
        setFilteredFields(bundleFilters[selectedOptionOfDropdownFields.bundleType] || []);
      } else {
        setFilteredFields([]);
      }
    }
  }, [form, isFilteringDrawerOpened, selectedOptionOfDropdownFields, bundleFilters]);

  // Update filteredFields when bundleType changes in the form
  const handleBundleTypeChange = (value) => {
    setFilteredFields(bundleFilters[value] || []);
    // Clear dependent fields when bundleType changes
    form.setFieldsValue({
      cardType: null,
      numberOfTrips: null,
      bundleValidityFrom: null,
      bundleValidityTo: null,
    });
  };

  const onResetForm = () => {
    form.resetFields();
    setFilteredFields([]);
    setSelectedOptionOfDropdownFields({
      bundleType: null,
      cardType: null,
      numberOfTrips: null,
      bundleValidityFrom: null,
      bundleValidityTo: null,
    });
    resetFilter();
  };

  const onSubmit = () => {
    const values = form.getFieldsValue();
    const formattedValues = {
      bundleType: values.bundleType || null,
      cardType: values.cardType || null,
      numberOfTrips: values.numberOfTrips || null,
      bundleValidityFrom: values.bundleValidityFrom
        ? values.bundleValidityFrom.format("YYYY-MM-DD")
        : null,
      bundleValidityTo: values.bundleValidityTo
        ? values.bundleValidityTo.format("YYYY-MM-DD")
        : null,
    };
    setSelectedOptionOfDropdownFields(formattedValues); // Update state before saving
    onSave(formattedValues);
  };

  return (
    <Drawer
      title={title ? title : "Filter"}
      placement="right"
      closable={false}
      onClose={closeFilteringDrawer}
      width={400}
      open={isFilteringDrawerOpened}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          name={formName}
          layout="vertical"
          onFinish={onSubmit}
          onFinishFailed={() => {}}
          autoComplete="off"
        >
          <Form.Item
            label={t("bundleScreen.filterDrawer.type")}
            name="bundleType"
          >
            <Select
              showSearch
              optionFilterProp="children"
              allowClear
              onChange={handleBundleTypeChange}
            >
              {bundleTypes.map((type) => (
                <Select.Option key={type.value} value={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {filteredFields.map((field) => (
            <Form.Item key={field.key} label={field.label} name={field.key}>
              {field.component}
            </Form.Item>
          ))}

          <Form.Item style={{ float: "right" }}>
            <Button
              type="default"
              style={{ marginBottom: 32, marginRight: 8 }}
              onClick={() => closeFilteringDrawer()}
            >
              {t("drawerButtons.cancel")}
            </Button>
            <Button
              type="primary"
              style={{ marginBottom: 32 }}
              htmlType="submit"
            >
              {t("drawerButtons.filter")}
            </Button>
          </Form.Item>
          <Form.Item style={{ float: "left" }}>
            <Button
              type="default"
              style={{ marginBottom: 32, marginRight: 8 }}
              onClick={onResetForm}
            >
              {t("drawerButtons.resetFilter")}
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default FilteringDrawer;